import { useHeaderButton } from "@/contexts/header-button-context";
import { GroupForm } from "../components/approval-group-form";
import { GroupUsersList } from "../components/approval-group-users";
import {
  GroupFormProvider,
  useApprovalGroupForm,
} from "../components/approval-group-context";
import { useEffect, useCallback, useMemo, useState } from "react";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import { useNavigate, useParams } from "react-router";
import { cn } from "@/utils/theme";
import type {
  ApprovalGroupFormData,
  ApprovalGroupType,
  ApprovalUser,
  ApprovalGroupLevel,
  SeniorityValues,
} from "@/types/entities/approval-groups";
import {
  useGetApprovalGroup,
  useUpdateApprovalGroup,
  useSyncMembersToGroup,
  useCreateGroupWorkload,
} from "@/queries/approval-groups";
import { AxiosError } from "axios";
import { isAPIError } from "@/utils/errors";

const MEMBER_LEVEL_VALUES: Record<string, number> = {
  junior: 4,
  medio: 3,
  senior: 2,
  lider: 1,
};

const getMemberLevelValue = (level: string | undefined): number =>
  MEMBER_LEVEL_VALUES[level ?? ""] ?? 1;

// Only send changed fields to avoid unique validation issues
const buildChangedFields = (
  data: ApprovalGroupFormData,
  groupData: ApprovalGroupType | undefined,
): Partial<ApprovalGroupFormData> => {
  const changedFields: Partial<ApprovalGroupFormData> = {};
  if (!groupData) return changedFields;

  changedFields.name = data.name;
  if (data.description !== (groupData.description || "")) {
    changedFields.description = data.description;
  }
  if (data.type !== (groupData.type || "approval")) {
    changedFields.type = data.type;
  }
  return changedFields;
};

const EditApprovalGroupInner = () => {
  const { setComp } = useHeaderButton();
  const navigate = useNavigate();
  const canUpdateGroups = true;
  const { canSave, isSubmitting, setFormData, setSelectedUsers } =
    useApprovalGroupForm();

  const { id: groupId } = useParams<{ id: string }>();

  const { data: groupData, isLoading } = useGetApprovalGroup(groupId ?? "");

  useEffect(() => {
    if (groupData) {
      setFormData({
        name: groupData.name,
        description: groupData.description || "",
        type: groupData.type || "approval",
        seniorityValues: groupData.workloads?.map((workload) => ({
          [workload.seniority]: { amount: workload.amount },
        })),
      });
      // Set the current group members as selected users
      const currentMembers = groupData.members || [];

      // Ensure each member has a level (set default if not provided)
      const membersWithLevels = currentMembers.map((member) => {
        let value = "";
        switch (member.seniority) {
          case 1:
            value = "lider";
            break;
          case 2:
            value = "senior";
            break;
          case 3:
            value = "medio";
            break;
          case 4:
            value = "junior";
            break;
          default:
            value = "medio"; // Default level if not provided
        }
        return {
          id: member.id,
          name: member.name || "",
          last_name: member.last_name || "",
          full_name: `${member.name} ${member.last_name}`.trim(),
          email: member.email || "",
          level: value as ApprovalGroupLevel,
        };
      });

      setSelectedUsers(membersWithLevels);
    }
  }, [groupData, setFormData, setSelectedUsers]);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const buttonElement = useMemo(
    () => (
      <>
        <Button variant="quiet" size="xs" type="button" onClick={handleCancel}>
          Cancelar
          <Icon type="far" name="times" />
        </Button>
        <Button
          variant="default"
          size="xs"
          form="new-group-form"
          type="submit"
          disabled={!canSave || !canUpdateGroups}
        >
          Guardar cambios
          <Icon
            type="fas"
            name={isSubmitting ? "spinner-third" : "save"}
            className={cn("text-current", isSubmitting && "animate-spin")}
          />
        </Button>
      </>
    ),
    [handleCancel, canSave, isSubmitting, canUpdateGroups],
  );

  useEffect(() => {
    setComp(buttonElement);
    return () => {
      setComp(null);
    };
  }, [setComp, buttonElement]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon
            type="fas"
            name="spinner-third"
            className="animate-spin text-2xl mb-2"
          />
          <p>Cargando datos del grupo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 tablet:grid-cols-2 gap-8">
      <GroupForm readonly={!canUpdateGroups} />
      <GroupUsersList readonly={!canUpdateGroups} />
    </div>
  );
};

const EditApprovalGroupContent = () => {
  const { id: groupId } = useParams<{ id: string }>();
  const { data: groupData } = useGetApprovalGroup(groupId ?? "");
  const { mutateAsync: updateGroup } = useUpdateApprovalGroup(groupId ?? "");
  const { mutateAsync: syncMembers } = useSyncMembersToGroup(groupId ?? "");
  const { mutateAsync: createWorkload } = useCreateGroupWorkload();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({});
  const clearErrors = useCallback(() => setApiErrors({}), []);

  const handleUpdateGroup = useCallback(
    async (
      data: ApprovalGroupFormData,
      users: ApprovalUser[],
      seniorityValues: SeniorityValues,
    ) => {
      try {
        clearErrors();

        const leaderCount = users.filter(
          (user) => user.level === "lider",
        ).length;

        if (leaderCount > 1) {
          showToast({
            title: "Error al actualizar grupo",
            description:
              "Solo puede haber un Líder por grupo. Por favor, ajuste los niveles de los usuarios seleccionados.",
            options: {
              type: "error",
              duration: 5000,
            },
          });
          return;
        }

        const changedFields = buildChangedFields(data, groupData);

        // Only update if there are changes
        if (Object.keys(changedFields).length > 0) {
          await updateGroup(changedFields);
        }

        // Sync members if there are users selected
        if (users && users.length > 0) {
          await syncMembers({
            members: users.map((user) => {
              const value = getMemberLevelValue(user.level);
              return {
                id: user.id,
                level: value,
                priority: value,
                seniority: value,
              };
            }),
          });
        }

        if (groupId) {
          await createWorkload({
            id: groupId,
            seniorityValues,
          });
        }

        showToast({
          title: "Grupo actualizado",
          description:
            "Los datos del grupo y/o sus miembros han sido actualizados exitosamente.",
          options: {
            type: "success",
            duration: 5000,
            customIcon: (
              <Avatar size="xs" shape="square">
                <AvatarFallback color="action" shape="square" type="icon">
                  <Icon type="fas" name="save" />
                </AvatarFallback>
              </Avatar>
            ),
          },
        });
        navigate(-1);
      } catch (error) {
        if (error instanceof AxiosError) {
          if (isAPIError(error?.response?.data, "name", "validation.unique")) {
            setApiErrors({ name: "Este nombre ya está en uso." });
          }
        }
        showToast({
          title: "Error al actualizar grupo",
          description: "No se pudo actualizar el grupo. Inténtalo de nuevo.",
          options: {
            type: "error",
            duration: 5000,
          },
        });
      }
    },
    [
      updateGroup,
      syncMembers,
      showToast,
      navigate,
      groupData,
      clearErrors,
      setApiErrors,
      groupId,
      createWorkload,
    ],
  );

  console.log(apiErrors);

  return (
    <GroupFormProvider
      initValues={{
        formDataState: { name: "", description: "", type: "approval" },
        selectedUsers: [],
      }}
      onSave={handleUpdateGroup}
      apiErrors={apiErrors}
      clearApiErrors={clearErrors}
    >
      <EditApprovalGroupInner />
    </GroupFormProvider>
  );
};

export const EditApprovalGroup = () => {
  return <EditApprovalGroupContent />;
};
